import React, { useState, useEffect, useCallback } from "react"
import Back from "../common/back/Back"
import Heading from "../common/heading/Heading"
import AuthModal from "../auth/AuthModal"
import EnrollModal from "../enrollment/EnrollModal"
import { useAuth } from "../../context/AuthContext"
import { useConfig } from "../../context/ConfigContext"
import API from "../../api/axios"
import "./courses.css"

const levelLabel = {
  beginner:     "Beginner",
  intermediate: "Intermediate",
  advanced:     "Advanced",
  all:          "All Levels",
}

/* ── Single course card — matches screenshot layout ── */
const CourseCard = ({ course, onEnroll }) => {
  const { paymentEnabled } = useConfig()
  const isFree  = course.isFree || course.isTrial
  const showComingSoon = !isFree && !paymentEnabled
  const level   = levelLabel[course.level] || course.level
  const stars   = Array.from({ length: 5 }, (_, i) => i < Math.round(course.rating || 5))
  const totalLessons = course.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || 0

  return (
    <div className={`course-card ${course.isFeatured ? "course-card--featured" : ""}`}>
      {/* Badges */}
      {course.isTrial    && <div className="course-card-badge trial"><i className="fa fa-gift"></i> Free Trial</div>}
      {course.isFeatured && !course.isTrial && <div className="course-card-badge popular">⭐ Popular</div>}
      {course.isFree && !course.isTrial && <div className="course-card-badge free"><i className="fa fa-unlock"></i> Free</div>}

      {/* Top: icon + title */}
      <div className="course-card-top">
        <div className="course-card-icon">
          <i className="fa fa-chart-line"></i>
        </div>
        <div>
          <h3 className="course-card-title">{course.title}</h3>
          {course.category && <span className="course-card-category">{course.category}</span>}
        </div>
      </div>

      {/* Stars + instructor */}
      <div className="course-card-meta">
        <div className="course-card-stars">
          {stars.map((filled, i) => (
            <i key={i} className={`fa fa-star${filled ? "" : "-o"}`}></i>
          ))}
          <span>({course.ratingCount > 0 ? course.ratingCount : "5.0"})</span>
        </div>
        <div className="course-card-instructor">
          <div className="course-card-avatar"><i className="fa fa-user-tie"></i></div>
          <span>by {course.instructor || "Mr. Ali"}</span>
        </div>
      </div>

      {/* Duration · Level */}
      <div className="course-card-duration">
        {course.duration && <><i className="fa fa-clock"></i><span>{course.duration}</span><span className="course-card-dot">·</span></>}
        <i className="fa fa-signal"></i><span>{level}</span>
      </div>

      {/* Short description */}
      {(course.shortDesc || course.description) && (
        <p className="course-card-desc">
          {course.shortDesc || course.description?.slice(0, 130) + "…"}
        </p>
      )}

      {/* Stats row: modules · lessons · enrolled */}
      <div className="course-card-stats">
        {course.modules?.length > 0 && (
          <span><i className="fa fa-layer-group"></i> {course.modules.length} modules</span>
        )}
        {totalLessons > 0 && (
          <span><i className="fa fa-play-circle"></i> {totalLessons} lessons</span>
        )}
        {course.enrollCount > 0 && (
          <span><i className="fa fa-users"></i> {course.enrollCount} enrolled</span>
        )}
      </div>

      {/* What you'll learn */}
      {Array.isArray(course.whatYouLearn) && course.whatYouLearn.length > 0 && (
        <ul className="course-card-learn">
          {course.whatYouLearn.slice(0, 3).map((item, i) => (
            <li key={i}><i className="fa fa-check-circle"></i>{item}</li>
          ))}
          {course.whatYouLearn.length > 3 && (
            <li className="course-card-learn-more">+ {course.whatYouLearn.length - 3} more outcomes</li>
          )}
        </ul>
      )}

      {/* Requirements */}
      {Array.isArray(course.requirements) && course.requirements.length > 0 && (
        <div className="course-card-requirements">
          <span className="course-card-req-label"><i className="fa fa-info-circle"></i> Requirements:</span>
          {course.requirements.slice(0, 2).map((r, i) => (
            <span key={i} className="course-card-req-item">{r}</span>
          ))}
        </div>
      )}

      {/* Price bar */}
      <div className="course-card-price">
        {isFree ? (
          <span className="course-card-price-free">
            {course.isTrial ? "Free Trial Access" : "Free Course"}
            {course.isTrial && course.trialDays > 0 && ` · ${course.trialDays} days`}
          </span>
        ) : (
          <>
            <span>KES {Number(course.price).toLocaleString()} Full</span>
            <span className="course-card-price-sep">/</span>
            <span className="course-card-price-flex">Flexible payment</span>
          </>
        )}
      </div>

      {/* Enroll button */}
      <button
        className={`course-card-enroll ${showComingSoon ? "coming-soon-btn" : ""}`}
        onClick={() => onEnroll(course)}
      >
        {isFree
          ? "START FREE !"
          : showComingSoon
            ? <><i className="fa fa-clock"></i> Coming Soon</>
            : "ENROLL NOW !"
        }
      </button>
    </div>
  )
}

/* ── Main page ── */
const CourseHome = () => {
  const { isUser }         = useAuth()
  const { paymentEnabled } = useConfig()

  const [courses,     setCourses]     = useState([])
  const [filtered,    setFiltered]    = useState([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState("")
  const [search,      setSearch]      = useState("")
  const [levelFilter, setLevelFilter] = useState("all")
  // Auth modal (for guests)
  const [authOpen,    setAuthOpen]    = useState(false)
  // Enroll modal (for logged-in users)
  const [enrollOpen,  setEnrollOpen]  = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)

  const fetchCourses = useCallback(async () => {
    try {
      const { data } = await API.get("/courses")
      setCourses(data.data || [])
      setFiltered(data.data || [])
    } catch {
      setError("Failed to load courses. Please refresh.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchCourses() }, [fetchCourses])

  // Filter
  useEffect(() => {
    let result = [...courses]
    if (levelFilter !== "all") result = result.filter(c => c.level === levelFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      result  = result.filter(c =>
        c.title.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q)
      )
    }
    setFiltered(result)
  }, [search, levelFilter, courses])

  const handleEnroll = (course) => {
    if (isUser) {
      setSelectedCourse(course)
      setEnrollOpen(true)
    } else {
      setAuthOpen(true)
    }
  }

  // Group: trial → featured → rest
  const trial    = filtered.filter(c => c.isTrial)
  const featured = filtered.filter(c => c.isFeatured && !c.isTrial)
  const rest     = filtered.filter(c => !c.isFeatured && !c.isTrial)
  const ordered  = [...trial, ...featured, ...rest]

  return (
    <>
      <Back title="Explore Courses" />

      <section className="courses-page padding">
        <div className="container">

          {/* Search + Filter */}
          <div className="courses-toolbar">
            <div className="courses-search-wrap">
              <i className="fa fa-search"></i>
              <input
                type="text"
                placeholder="Search courses…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="courses-search-input"
              />
              {search && (
                <button className="courses-clear-btn" onClick={() => setSearch("")}>
                  <i className="fa fa-times"></i>
                </button>
              )}
            </div>
            <div className="courses-filter-tabs">
              {["all", "beginner", "intermediate", "advanced"].map(l => (
                <button
                  key={l}
                  className={`courses-filter-tab ${levelFilter === l ? "active" : ""}`}
                  onClick={() => setLevelFilter(l)}
                >
                  {l === "all" ? "All Levels" : levelLabel[l]}
                </button>
              ))}
            </div>
          </div>

          {/* States */}
          {loading && (
            <div className="courses-state">
              <i className="fa fa-spinner fa-spin"></i>
              <span>Loading courses…</span>
            </div>
          )}
          {!loading && error && (
            <div className="courses-state error">
              <i className="fa fa-exclamation-triangle"></i>
              <span>{error}</span>
              <button onClick={fetchCourses}>Retry</button>
            </div>
          )}
          {!loading && !error && ordered.length === 0 && (
            <div className="courses-state">
              <i className="fa fa-search"></i>
              <h3>No courses found</h3>
              <p>Try a different search or filter.</p>
              <button onClick={() => { setSearch(""); setLevelFilter("all") }}>Clear filters</button>
            </div>
          )}

          {/* Cards */}
          {!loading && !error && ordered.length > 0 && (
            <>
              {trial.length > 0 && (
                <>
                  <Heading subtitle="START FOR FREE" title="Free Trial Access" />
                  <div className="courses-grid">
                    {trial.map(c => <CourseCard key={c.id} course={c} onEnroll={handleEnroll} />)}
                  </div>
                </>
              )}

              {(featured.length > 0 || rest.length > 0) && (
                <>
                  <Heading subtitle="OUR PROGRAMMES" title="All Training Courses" />
                  <div className="courses-grid">
                    {[...featured, ...rest].map(c => (
                      <CourseCard key={c.id} course={c} onEnroll={handleEnroll} />
                    ))}
                  </div>
                </>
              )}

              {(search || levelFilter !== "all") && (
                <p className="courses-result-count">
                  Showing {ordered.length} of {courses.length} courses
                </p>
              )}
            </>
          )}

          {/* Coming Soon contact strip — shown when payments are disabled */}
          {!paymentEnabled && (
            <div className="coming-soon-strip">
              <div className="coming-soon-strip-icon"><i className="fa fa-clock"></i></div>
              <div className="coming-soon-strip-text">
                <strong>Online Payments Coming Soon</strong>
                <span>To enroll in a paid course, contact us via WhatsApp or email and we'll get you set up.</span>
              </div>
              <div className="coming-soon-strip-actions">
                <a href="https://wa.me/254722525826" target="_blank" rel="noreferrer" className="strip-wa-btn">
                  <i className="fab fa-whatsapp"></i> WhatsApp
                </a>
                <a href="mailto:info@rovers.ke" className="strip-email-btn">
                  <i className="fa fa-envelope"></i> Email Us
                </a>
              </div>
            </div>
          )}

        </div>
      </section>

      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} mode="register" />
      <EnrollModal
        course={selectedCourse}
        isOpen={enrollOpen}
        onClose={() => { setEnrollOpen(false); setSelectedCourse(null) }}
      />
    </>
  )
}

export default CourseHome
